import { useCallback, useEffect, useRef, useState } from 'react';

type SnapScrollDirection = 'x' | 'y';

type UseSnapScrollOptions = {
  direction: SnapScrollDirection;
  itemCount: number;
  initialIndex?: number;
  onIndexChange?: (index: number) => void;
  enabled?: boolean;
};

type UseSnapScrollReturn = {
  scrollerRef: (el: HTMLDivElement | null) => void;
  setSlideRef: (index: number) => (el: HTMLDivElement | null) => void;
  activeIndex: number;
  scrollToIndex: (index: number, options?: { behavior?: ScrollBehavior }) => void;
  scrollToNext: () => void;
  scrollToPrevious: () => void;
  isScrolling: boolean;
};

type ScrollState = 'idle' | 'scrolling' | 'settling';

const SETTLING_MS = 50;
const SCROLLEND_FALLBACK_MS = 1000;
const DISCRETE_MAGNITUDE_THRESHOLD = 80;
const DISCRETE_TIMING_GAP_MS = 80;
const CROSS_AXIS_RATIO_THRESHOLD = 2.0;
const RAPID_SCROLL_DEBOUNCE_MS = 50;

/**
 * Classifies a wheel event as discrete (mouse wheel tick) vs continuous (trackpad).
 *
 * Dual-signal: BOTH timing gap AND magnitude must agree.
 * Exception: deltaMode !== 0 is always discrete (DOM_DELTA_LINE/PAGE).
 * Exception: first event (lastWheelTime=0) uses magnitude only.
 */
function isDiscreteWheelEvent(e: WheelEvent, primaryDelta: number, lastWheelTime: number, now: number): boolean {
  if (e.deltaMode !== 0) return true;

  const gap = lastWheelTime === 0 ? Infinity : now - lastWheelTime;
  const absDelta = Math.abs(primaryDelta);
  const isInteger = Number.isInteger(primaryDelta);

  const magnitudeDiscrete = isInteger && absDelta >= DISCRETE_MAGNITUDE_THRESHOLD;
  const timingDiscrete = gap > DISCRETE_TIMING_GAP_MS;

  if (lastWheelTime === 0) return magnitudeDiscrete;

  return magnitudeDiscrete && timingDiscrete;
}

export const useSnapScroll = ({
  direction,
  itemCount,
  initialIndex = 0,
  onIndexChange,
  enabled = true,
}: UseSnapScrollOptions): UseSnapScrollReturn => {
  // Callback ref pattern: storing the element in state triggers effect re-runs
  const [scrollerEl, setScrollerEl] = useState<HTMLDivElement | null>(null);
  const scrollerRef = useCallback((el: HTMLDivElement | null) => {
    scrollerElRef.current = el;
    setScrollerEl(el);
  }, []);

  const slideRefsMap = useRef<Map<number, HTMLDivElement>>(new Map());
  const slideRefCallbacksMap = useRef<Map<number, (el: HTMLDivElement | null) => void>>(new Map());

  const stateRef = useRef<ScrollState>('idle');
  const [isScrolling, setIsScrolling] = useState(false);

  const targetIndexRef = useRef(initialIndex);
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  const lastWheelTimeRef = useRef(0);
  const fallbackTimerRef = useRef<number | null>(null);
  const settlingTimerRef = useRef<number | null>(null);
  const scrollEndHandlerRef = useRef<((e: Event) => void) | null>(null);
  const debouncedScrollTimerRef = useRef<number | null>(null);

  const onIndexChangeRef = useRef(onIndexChange);
  onIndexChangeRef.current = onIndexChange;
  const itemCountRef = useRef(itemCount);
  itemCountRef.current = itemCount;
  const directionRef = useRef(direction);
  directionRef.current = direction;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  // Keep a ref to scrollerEl for use in callbacks that don't depend on it
  const scrollerElRef = useRef(scrollerEl);
  scrollerElRef.current = scrollerEl;

  const setSlideRef = useCallback((index: number) => {
    let cb = slideRefCallbacksMap.current.get(index);
    if (!cb) {
      cb = (el: HTMLDivElement | null) => {
        if (el) {
          slideRefsMap.current.set(index, el);
        } else {
          slideRefsMap.current.delete(index);
          slideRefCallbacksMap.current.delete(index);
        }
      };
      slideRefCallbacksMap.current.set(index, cb);
    }
    return cb;
  }, []);

  const clearTimers = useCallback(() => {
    if (fallbackTimerRef.current !== null) {
      window.clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    if (settlingTimerRef.current !== null) {
      window.clearTimeout(settlingTimerRef.current);
      settlingTimerRef.current = null;
    }
    if (debouncedScrollTimerRef.current !== null) {
      window.clearTimeout(debouncedScrollTimerRef.current);
      debouncedScrollTimerRef.current = null;
    }
  }, []);

  const transitionToIdle = useCallback(() => {
    stateRef.current = 'idle';
    setIsScrolling(false);
  }, []);

  const transitionToSettling = useCallback(() => {
    stateRef.current = 'settling';
    const scroller = scrollerElRef.current;
    if (scroller) {
      scroller.style.scrollSnapType = '';
    }
    settlingTimerRef.current = window.setTimeout(() => {
      settlingTimerRef.current = null;
      transitionToIdle();
    }, SETTLING_MS);
  }, [transitionToIdle]);

  const executeScroll = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth') => {
      const scroller = scrollerElRef.current;
      const slide = slideRefsMap.current.get(index);
      if (!scroller || !slide) return;

      const dir = directionRef.current;
      const scrollPosition = dir === 'y' ? slide.offsetTop : slide.offsetLeft;
      const scrollOpts: ScrollToOptions = {
        [dir === 'y' ? 'top' : 'left']: scrollPosition,
      };

      targetIndexRef.current = index;

      const effectiveBehavior =
        behavior === 'smooth' && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : behavior;

      if (effectiveBehavior === 'instant') {
        scroller.scrollTo({ ...scrollOpts, behavior: 'instant' });
        setActiveIndex(index);
        onIndexChangeRef.current?.(index);
        return;
      }

      clearTimers();

      // Remove previous scrollend handler to prevent double-firing on rapid calls
      if (scrollEndHandlerRef.current) {
        scroller.removeEventListener('scrollend', scrollEndHandlerRef.current);
        scrollEndHandlerRef.current = null;
      }

      stateRef.current = 'scrolling';
      setIsScrolling(true);

      scroller.style.scrollSnapType = 'none';
      scroller.scrollTo({ ...scrollOpts, behavior: 'smooth' });

      const handleScrollEnd = () => {
        scrollEndHandlerRef.current = null;
        if (fallbackTimerRef.current !== null) {
          window.clearTimeout(fallbackTimerRef.current);
          fallbackTimerRef.current = null;
        }
        transitionToSettling();
      };

      scrollEndHandlerRef.current = handleScrollEnd;
      scroller.addEventListener('scrollend', handleScrollEnd, { once: true });

      fallbackTimerRef.current = window.setTimeout(() => {
        fallbackTimerRef.current = null;
        scroller.removeEventListener('scrollend', handleScrollEnd);
        scrollEndHandlerRef.current = null;
        transitionToSettling();
      }, SCROLLEND_FALLBACK_MS);
    },
    [clearTimers, transitionToSettling],
  );

  // Wheel handler — only intercept discrete (mouse wheel) events.
  // Trackpad/continuous scrolling is handled natively by CSS scroll-snap,
  // matching the Carousel component's natural feel.
  useEffect(() => {
    if (!scrollerEl || !enabled || itemCount === 0) return;

    const handleWheel = (e: WheelEvent) => {
      if (!enabledRef.current) return;

      const dir = directionRef.current;
      const primaryDelta = dir === 'y' ? e.deltaY : e.deltaX;
      const crossDelta = dir === 'y' ? e.deltaX : e.deltaY;

      if (Math.abs(crossDelta) > Math.abs(primaryDelta)) return;

      if (Math.abs(crossDelta) > 0 && Math.abs(primaryDelta) / Math.abs(crossDelta) < CROSS_AXIS_RATIO_THRESHOLD) {
        return;
      }

      // During programmatic scrolling, block all wheel events to prevent conflicts
      if (stateRef.current !== 'idle') {
        e.preventDefault();
        return;
      }

      const now = Date.now();
      const discrete = isDiscreteWheelEvent(e, primaryDelta, lastWheelTimeRef.current, now);
      lastWheelTimeRef.current = now;

      if (discrete) {
        // Mouse wheel tick: programmatically scroll one card (native snap
        // can't reliably advance full-viewport cards from a single tick)
        e.preventDefault();

        const scrollDirection = primaryDelta > 0 ? 1 : -1;
        const nextIndex = targetIndexRef.current + scrollDirection;

        if (nextIndex < 0 || nextIndex >= itemCountRef.current) return;

        executeScroll(nextIndex);
      }

      // Trackpad/continuous: do nothing — native CSS scroll-snap handles it
    };

    const handleTouchStart = () => {
      if (stateRef.current !== 'scrolling') return;
      clearTimers();
      transitionToSettling();
    };

    scrollerEl.addEventListener('wheel', handleWheel, { passive: false });
    scrollerEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    return () => {
      scrollerEl.removeEventListener('wheel', handleWheel);
      scrollerEl.removeEventListener('touchstart', handleTouchStart);
    };
  }, [scrollerEl, enabled, itemCount, executeScroll, clearTimers, transitionToSettling]);

  // IntersectionObserver
  useEffect(() => {
    if (!scrollerEl || !enabled || itemCount === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const target = entry.target as HTMLDivElement;
            let foundIndex = -1;
            for (const [idx, el] of slideRefsMap.current.entries()) {
              if (el === target) {
                foundIndex = idx;
                break;
              }
            }
            if (foundIndex === -1) continue;

            setActiveIndex(foundIndex);
            onIndexChangeRef.current?.(foundIndex);

            if (stateRef.current === 'idle') {
              targetIndexRef.current = foundIndex;
            }
          }
        }
      },
      { root: scrollerEl, threshold: [0.5] },
    );

    for (const slide of slideRefsMap.current.values()) {
      observer.observe(slide);
    }

    return () => observer.disconnect();
  }, [scrollerEl, enabled, itemCount]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimers();
      stateRef.current = 'idle';
    };
  }, [clearTimers]);

  // Reset when disabled
  useEffect(() => {
    if (!enabled) {
      clearTimers();
      stateRef.current = 'idle';
      setIsScrolling(false);
      if (scrollerEl) {
        scrollerEl.style.scrollSnapType = '';
      }
    }
  }, [enabled, scrollerEl, clearTimers]);

  const scrollToIndex = useCallback(
    (index: number, options?: { behavior?: ScrollBehavior }) => {
      if (itemCountRef.current <= 0 || !Number.isFinite(index)) return;
      const clamped = Math.max(0, Math.min(index, itemCountRef.current - 1));
      executeScroll(clamped, options?.behavior ?? 'smooth');
    },
    [executeScroll],
  );

  // Debounced scroll for rapid keyboard/button input: pressing down 5 times
  // quickly batches into one smooth scroll to card +5 instead of 5 jittery animations.
  const scheduleScroll = useCallback(
    (index: number) => {
      targetIndexRef.current = index;
      if (debouncedScrollTimerRef.current !== null) {
        window.clearTimeout(debouncedScrollTimerRef.current);
      }
      debouncedScrollTimerRef.current = window.setTimeout(() => {
        debouncedScrollTimerRef.current = null;
        executeScroll(targetIndexRef.current);
      }, RAPID_SCROLL_DEBOUNCE_MS);
    },
    [executeScroll],
  );

  const scrollToNext = useCallback(() => {
    const next = targetIndexRef.current + 1;
    if (next >= itemCountRef.current) return;
    scheduleScroll(next);
  }, [scheduleScroll]);

  const scrollToPrevious = useCallback(() => {
    const prev = targetIndexRef.current - 1;
    if (prev < 0) return;
    scheduleScroll(prev);
  }, [scheduleScroll]);

  return {
    scrollerRef,
    setSlideRef,
    activeIndex,
    scrollToIndex,
    scrollToNext,
    scrollToPrevious,
    isScrolling,
  };
};
